<template>
  <el-drawer
    :visible.sync="showDrawer"
    :before-close="handleClose"
    :size="drawerWidth"
    :show-close="false"
    :show-header="false"
  >
    <div class="h-full w-full flex flex-col justify-between">
      <div>
        <div class="flex items-center justify-between border-b pb-6 px-6">
          <h2>User Details</h2>
          <span class="cursor-pointer" @click="handleClose">
            <close-icon size="h-6 w-6" />
          </span>
        </div>

        <div class="px-6 py-4 sm:py-5 flex items-center gap-5 border-b">
          <div
            class="relative uppercase h-14 w-14 flex items-center justify-center rounded-full font-bold text-xl bg-gray-25 text-white"
          >
            {{ getUserInitials }}
            <span
              :class="userStatus(details.type)"
              class="absolute bottom-0 -right-1 w-5 h-5 rounded-full border-2"
            ></span>
          </div>

          <div class="text-sm">
            <div class="font-bold capitalize">
              {{ details.firstName || '--' }}
              {{ details.lastName || '--' }}
            </div>
            <p class="text-gray-muted font-normal">
              {{ details.email || '--' }}
            </p>
          </div>
        </div>

        <div
          class="px-6 py-4 sm:py-5 flex items-center border-b font-normal text-sm"
        >
          <span class="w-1/3">Status</span>
          <div
            class="rounded-lg py-1.5 w-20 flex items-center justify-center text-xs"
            :class="statusClass(details.type)"
          >
            <span class="capitalize font-bold">
              {{ details.type }}
            </span>
          </div>
        </div>

        <div
          class="px-6 py-4 sm:py-5 flex items-center border-b font-normal text-sm"
        >
          <span class="w-1/3">Role</span>

          <span>{{ accessType(details.kind) }}</span>
        </div>
        <div
          class="px-6 py-4 sm:py-5 flex items-center border-b font-normal text-sm"
        >
          <span class="w-1/3"
            >{{
              details.type === $STATUS.PENDING ? 'Date Invited' : 'Last Active'
            }}
          </span>

          <span>{{ getLastActiveDate(details) }}</span>
        </div>

        <div
          v-if="details.kind === 3 || details.kind === 5"
          class="px-6 py-4 sm:py-5 flex items-center border-b font-normal text-sm"
        >
          <span class="w-1/3">Transaction Limit</span>

          <span
            >{{
              details.transactionLimit
                ? `$ ${parseAmount(details.transactionLimit)}`
                : '--'
            }}
          </span>
        </div>

        <div v-for="(role, index) in roles" :key="index">
          <div
            v-if="role.value === details.kind"
            class="py-4 sm:py-5 px-4 mx-6 my-5 bg-gray-24 flex gap-3 rounded-lg"
          >
            <span>
              <info-icon color="#5D5E68" />
            </span>

            <div>
              <h2 class="font-semibold text-sm">{{ role.title }}</h2>
              <p class="mt-1 text-gray-muted font-normal text-sm">
                {{ role.body }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Buttons -->
      <div class="m-6 flex flex-col gap-3">
        <button
          v-if="details.type === $STATUS.PENDING"
          class="bg-green-9 text-green-8 border border-green-8"
          @click="resendUserInvite"
        >
          Resend Invite
        </button>

        <button
          v-if="!isEditable"
          class="text-white bg-primary"
          @click="handleEditReactivate"
        >
          {{
            details.type === $STATUS.INACTIVE
              ? 'Reactivate User'
              : 'Edit Access'
          }}
        </button>

        <button
          v-if="!isOwner"
          class="bg-white text-pink-3 border border-pink-3"
          @click="handleCancelDeactivate"
        >
          {{ getButtonTitle }}
        </button>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import breakpoints from '@/utils/breakpoints.js'
import userStatus from '@/mixins/userKinds.js'
import statusClass from '@/mixins/global.js'
import app from '@/mixins/app.js'
import { ROLES } from '@/utils/constants.js'
import { FormatServerName, ParseAmount } from '@/utils/helpers.js'

export default {
  name: 'UserDetailsDrawer',
  mixins: [userStatus, statusClass, app],
  components: {
    CloseIcon: () => import('@/assets/v2/icons/CloseIcon.vue'),
    InfoIcon: () => import('@/assets/v2/icons/InfoTwoIcon.vue')
  },
  props: {
    showDrawer: {
      type: Boolean,
      required: true
    },
    details: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      breakpoints,
      roles: ROLES
    }
  },
  computed: {
    drawerWidth () {
      return breakpoints.lgAndUp ? '30%' : breakpoints.mdAndUp ? '55%' : '90%'
    },
    user () {
      return this.$store.state.auth.user
    },
    getUserInitials () {
      const firstName = this.details?.firstName || ''
      const lastName = this.details?.lastName || ''
      return (firstName[0] || '') + (lastName[0] || '')
    },
    getButtonTitle () {
      return this.details.type === this.$STATUS.PENDING
        ? 'Cancel'
        : this.details.type === this.$STATUS.INACTIVE
        ? 'Delete User'
        : 'Deactivate User'
    },
    isOwner () {
      return this.details.kind === 1
    },
    isEditable () {
      return this.isOwner || this.details.type === this.$STATUS.PENDING
    }
  },
  methods: {
    handleClose () {
      this.$emit('closeDrawer')
    },
    parseAmount (val) {
      return ParseAmount(val)
    },
    accessType (kind) {
      for (const role of ROLES) {
        if (kind === role.value) {
          return FormatServerName(role.label)
        }
      }
    },
    getLastActiveDate (details) {
      let formattedTime
      if (details.type === this.$STATUS.PENDING || !details.lastSeenAt) {
        formattedTime = details.createdAt
      } else {
        formattedTime = details.lastSeenAt
      }

      return this.tooltipDateContent(formattedTime)
    },
    resendUserInvite () {
      this.$emit('confirmResendUserInvite', this.details.email)
      this.handleClose()
    },
    cancelUserInvite () {
      this.$emit('confirmCancelInvitation', this.details.email)
      this.handleClose()
    },
    editUserAccess () {
      this.handleClose()
      this.$emit('openUserDrawer')
    },
    deactivateUser () {
      this.handleClose()
      this.$emit('confirmDeactivateUser', this.details.email)
    },
    deleteUser () {
      this.handleClose()
      this.$emit('confirmDeleteUser', this.details.email)
    },
    reactivateUser () {
      this.handleClose()
      this.$emit('confirmActivateUser', this.details.email)
    },
    handleEditReactivate () {
      if (this.details?.type === this.$STATUS.INACTIVE) {
        this.reactivateUser()
      } else {
        this.editUserAccess()
      }
    },
    handleCancelDeactivate () {
      if (this.details?.type === this.$STATUS.PENDING) {
        this.cancelUserInvite()
      } else if (this.details?.type === this.$STATUS.INACTIVE) {
        this.deleteUser()
      } else {
        this.deactivateUser()
      }
    }
  }
}
</script>

<style scoped>
button {
  height: 55px;
  font-size: 14px;
  border-radius: 8px;
  outline: 0;
}

button:active {
  transform: translateY(1px);
}
>>> .el-drawer__header {
  padding: 0;
}
>>> .el-drawer {
  background: var(--background-canvas) !important;
}
</style>
